// Header behaviors loaded with `defer`. Sticky-header logic stays inline in
// header.liquid because it has to run before the user can scroll past the
// announcement bar.

function updateHeaderCartState(itemCount) {
  if (!Number.isInteger(itemCount) || itemCount < 0) return;

  document.querySelectorAll('.header__action--cart').forEach((cartLink) => {
    const label = cartLink.dataset.cartLabel;
    const badge = cartLink.querySelector('.header__cart-count, [data-header-cart-count]');

    if (label) cartLink.setAttribute('aria-label', `${label} (${itemCount})`);
    if (badge) {
      badge.textContent = itemCount;
      badge.classList.toggle('hidden', itemCount === 0);
    }
  });
}

document.addEventListener('cart:updated', (event) => {
  updateHeaderCartState(event.detail?.item_count);
});

// Mobile Menu Toggle
const menuToggle = document.querySelector('.header__menu-toggle');
const mobileMenu = document.getElementById('mobile-menu');
const menuClose = document.querySelector('.mobile-menu__close');
const menuOverlay = document.querySelector('.mobile-menu__overlay');
let mobileMenuLayer = null;

const closeMenu = () => {
  if (!mobileMenu || mobileMenu.getAttribute('aria-hidden') !== 'false') return;
  mobileMenu.setAttribute('aria-hidden', 'true');
  mobileMenu.setAttribute('inert', '');
  menuToggle?.setAttribute('aria-expanded', 'false');

  if (mobileMenuLayer) {
    mobileMenuLayer.close({ fallbackFocus: () => menuToggle });
  } else {
    window.__scrollLock?.unlock(mobileMenu);
    window.DrhOverlayManager?.safeFocus(menuToggle) || menuToggle?.focus({ preventScroll: true });
  }
};

if (menuToggle && mobileMenu) {
  const drawer = mobileMenu.querySelector('.mobile-menu__drawer');
  const closeButton = mobileMenu.querySelector('.mobile-menu__close');
  if (window.DrhOverlayManager && drawer) {
    mobileMenuLayer = window.DrhOverlayManager.create({
      root: mobileMenu,
      panel: drawer,
      modal: true,
      onEscape: closeMenu,
      fallbackFocus: () => menuToggle,
    });
  }

  menuToggle.addEventListener('click', () => {
    mobileMenu.setAttribute('aria-hidden', 'false');
    mobileMenu.removeAttribute('inert');
    menuToggle.setAttribute('aria-expanded', 'true');
    if (mobileMenuLayer) {
      mobileMenuLayer.open({ trigger: menuToggle, initialFocus: closeButton });
    } else {
      window.__scrollLock?.lock(mobileMenu);
      window.DrhOverlayManager?.safeFocus(closeButton) || closeButton?.focus();
    }
  });

  if (menuClose) menuClose.addEventListener('click', closeMenu);
  if (menuOverlay) menuOverlay.addEventListener('click', closeMenu);
  if (!mobileMenuLayer) {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeMenu();
    });
  }
}

// Desktop mega-menu: hybrid disclosure navigation. The category label remains
// a native link; the adjacent button owns expanded state and Enter/Space.
// Hover is preserved for pointer users without intercepting link navigation.
// Motion layer (docs/audits/shopify-megamenu-motion-2026-09-29.md): one shared
// stage whose height follows the open panel, a direction-aware slide between
// menus, a short close grace across the gaps between items and an aim delay
// so a diagonal move into the open panel does not switch to a neighbour.
(() => {
  if (window.__drhDesktopMegaMenuController) {
    window.__drhDesktopMegaMenuController.init(document);
    return;
  }

  const states = new WeakMap();
  const initializedSidebars = new WeakSet();
  let openItem = null;

  const MOTION = {
    closeGraceMs: 180,
    aimDelayMs: 110,
    aimWindowMs: 100,
    shiftPx: 40,
    dropPx: 8,
    // Just after --mega-slide-duration (0.26s): the outgoing panel is hidden.
    shiftResetMs: 300,
  };
  const rootElement = document.documentElement;
  const reduceMotion = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  const trackedItems = new Set();
  const pointerTrail = [];
  let trailActive = false;
  let stage = null;
  let switching = false;
  let lastPointer = null;
  // Pointer position at the last explicit close. Closing a panel removes its
  // hover bridge, and browsers then report the item below the resting pointer
  // as entered. Such boundary events must not reopen a menu.
  let hoverLatch = null;

  const rememberPointer = (event) => {
    if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) {
      lastPointer = { x: event.clientX, y: event.clientY };
    }
  };

  const latchHover = (event) => {
    rememberPointer(event);
    hoverLatch = lastPointer ? { ...lastPointer } : null;
  };

  const blockedByLatch = (event) => {
    if (!hoverLatch) return false;
    const x = event?.clientX;
    const y = event?.clientY;
    if (Number.isFinite(x) && Number.isFinite(y)
      && Math.abs(x - hoverLatch.x) <= 2 && Math.abs(y - hoverLatch.y) <= 2) return true;
    hoverLatch = null;
    return false;
  };

  // Pointer history is only recorded while a menu is open (aim detection).
  const recordPointer = (event) => {
    rememberPointer(event);
    const now = performance.now();
    pointerTrail.push({ t: now, x: event.clientX, y: event.clientY });
    while (pointerTrail.length && now - pointerTrail[0].t > MOTION.aimWindowMs) pointerTrail.shift();
  };

  const setTrailActive = (active) => {
    if (active === trailActive) return;
    trailActive = active;
    pointerTrail.length = 0;
    if (active) document.addEventListener('pointermove', recordPointer, { passive: true });
    else document.removeEventListener('pointermove', recordPointer);
  };

  // Heading down (even at a shallow angle) within the last aim window means
  // "into the open panel". Stale samples never count.
  const movingIntoPanel = () => {
    const now = performance.now();
    const recent = pointerTrail.filter((sample) => now - sample.t <= MOTION.aimWindowMs);
    if (recent.length < 2) return false;
    const first = recent[0];
    const last = recent[recent.length - 1];
    const dx = last.x - first.x;
    const dy = last.y - first.y;
    return dy > 2 && dy >= Math.abs(dx) * 0.1;
  };

  const cancelPending = (item) => {
    const state = states.get(item);
    if (!state) return;
    if (state.leaveTimer) {
      // The pointer already left this item; finalize that without reopening.
      state.hovered = false;
    }
    window.clearTimeout(state.leaveTimer);
    window.clearTimeout(state.aimTimer);
    state.leaveTimer = null;
    state.aimTimer = null;
  };

  const cancelAllPending = () => {
    trackedItems.forEach((item) => {
      cancelPending(item);
      if (!item.isConnected) trackedItems.delete(item);
    });
  };

  const orderOf = (item) => Array.from(document.querySelectorAll('[data-mega-menu-item]')).indexOf(item);

  const setShift = (menu, x, y) => {
    menu.style.setProperty('--mega-shift-x', `${x}px`);
    menu.style.setProperty('--mega-shift-y', `${y}px`);
  };

  // After a switch the outgoing panel slides away with a sideways offset.
  // Once it is hidden, drop the offset without animating: a closed panel
  // shifted +40px would otherwise widen the page for horizontal scrolling.
  // Explicit closes do not cancel this; opening the panel again does.
  const scheduleShiftReset = (item, menu) => {
    const state = states.get(item);
    if (!state) return;
    window.clearTimeout(state.shiftResetTimer);
    state.shiftResetTimer = window.setTimeout(() => {
      state.shiftResetTimer = null;
      if (!item.isConnected || item.classList.contains('mega-menu--open')) return;
      const inner = menu.querySelector('.mega-menu__inner');
      const previousTransition = inner?.style.transition;
      menu.style.transition = 'none';
      if (inner) inner.style.transition = 'none';
      setShift(menu, 0, -MOTION.dropPx);
      menu.getBoundingClientRect(); // commit the reset before transitions return
      menu.style.removeProperty('transition');
      if (inner) {
        if (previousTransition) inner.style.transition = previousTransition;
        else inner.style.removeProperty('transition');
      }
    }, MOTION.shiftResetMs);
  };

  const cancelShiftReset = (item) => {
    const state = states.get(item);
    if (!state) return;
    window.clearTimeout(state.shiftResetTimer);
    state.shiftResetTimer = null;
  };

  // Panels are laid out (hidden) only after the first intent so that panels
  // with images do not load them on every page view.
  const warmUp = () => rootElement.classList.add('mega-motion-ready');

  const removeStage = () => {
    if (!stage) return;
    stage.parentElement?.classList.remove('mega-stage-host');
    stage.parentElement?.style.removeProperty('--mega-stage-h');
    stage.remove();
    stage = null;
  };

  const placeStage = (menu) => {
    const host = menu.offsetParent;
    if (!host) return null;
    if (!stage || !stage.isConnected || stage.parentElement !== host) {
      removeStage();
      stage = document.createElement('div');
      stage.className = `mega-stage ${Array.from(menu.classList).filter((name) => name.startsWith('color-scheme-')).join(' ')}`;
      stage.setAttribute('aria-hidden', 'true');
      host.classList.add('mega-stage-host');
      host.appendChild(stage);
    }
    stage.style.top = `${menu.offsetTop}px`;
    stage.style.left = `${menu.offsetLeft}px`;
    stage.style.width = `${menu.offsetWidth}px`;
    return stage;
  };

  const setStageHeight = (height, animate) => {
    if (!stage) return;
    if (!animate) rootElement.classList.add('mega-instant');
    // A single inherited animated value drives both the surface and clip.
    // Separate height/clip transitions diverge when a switch is interrupted.
    stage.parentElement.style.setProperty('--mega-stage-h', `${height}px`);
    if (!animate) {
      stage.getBoundingClientRect();
      rootElement.classList.remove('mega-instant');
    }
  };

  const prepareOpen = (item, previous) => {
    const { menu } = getParts(item);
    if (!menu) return;
    warmUp();
    cancelShiftReset(item);
    const direction = previous && !reduceMotion.matches ? Math.sign(orderOf(item) - orderOf(previous)) : 0;
    const stageWasOpen = Boolean(stage?.classList.contains('mega-stage--open'));
    placeStage(menu);
    if (previous) {
      const previousMenu = getParts(previous).menu;
      if (previousMenu) setShift(previousMenu, -direction * MOTION.shiftPx, 0);
    }
    setShift(menu, direction * MOTION.shiftPx, direction ? 0 : -MOTION.dropPx);
    menu.getBoundingClientRect(); // commit the start position before the open class lands
    setStageHeight(menu.offsetHeight, Boolean(previous) && stageWasOpen);
  };

  const resizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const item = entry.target.closest('[data-mega-menu-item]');
        if (item && item === openItem) setStageHeight(entry.target.offsetHeight, true);
      });
    })
    : null;

  const getParts = (item) => ({
    link: item.querySelector(':scope > .header__nav-link'),
    toggle: item.querySelector(':scope > .header__nav-toggle'),
    menu: item.querySelector(':scope > .mega-menu'),
  });

  const focusWithoutScroll = (element) => {
    if (!element?.isConnected) return;
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  const setExpanded = (item, expanded) => {
    const { toggle, menu } = getParts(item);
    const wasOpen = item.classList.contains('mega-menu--open');
    if (!expanded && wasOpen && !switching && menu) setShift(menu, 0, -MOTION.dropPx);
    if (!expanded && wasOpen && switching && menu) scheduleShiftReset(item, menu);
    item.classList.toggle('mega-menu--open', expanded);
    if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
    if (menu) {
      // Closed and fading panels are neither focusable nor clickable.
      if (expanded) menu.removeAttribute('inert');
      else menu.setAttribute('inert', '');
      if (resizeObserver && expanded !== wasOpen) {
        if (expanded) resizeObserver.observe(menu);
        else resizeObserver.unobserve(menu);
      }
    }
    if (expanded) {
      openItem = item;
    } else if (openItem === item) {
      openItem = null;
    }
    if (stage) stage.classList.toggle('mega-stage--open', Boolean(openItem));
    setTrailActive(Boolean(openItem));
  };

  const closeCompetingItem = (item) => {
    const state = states.get(item);
    if (!state) return;
    cancelPending(item);
    state.explicit = false;
    state.dismissed = true;
    item.classList.add('mega-menu--dismissed');
    setExpanded(item, false);
  };

  const syncItem = (item) => {
    const state = states.get(item);
    const { menu } = getParts(item);
    if (!state || !menu) return;

    const panelHasFocus = menu.contains(document.activeElement);
    const shouldOpen = !state.dismissed && (state.hovered || state.explicit || panelHasFocus);

    const previous = openItem && openItem !== item ? openItem : null;
    if (shouldOpen && !item.classList.contains('mega-menu--open')) prepareOpen(item, previous);
    if (shouldOpen && previous) {
      switching = true;
      closeCompetingItem(previous);
      switching = false;
    }
    setExpanded(item, shouldOpen);
  };

  const openMenu = (item, invoker, focusFirstLink = false) => {
    const state = states.get(item);
    const { menu } = getParts(item);
    if (!state || !menu) return;

    cancelAllPending();
    state.explicit = true;
    state.dismissed = false;
    state.invoker = invoker || state.invoker;
    item.classList.remove('mega-menu--dismissed');
    syncItem(item);

    if (focusFirstLink) {
      const firstContentLink = menu.querySelector('a[href]');
      if (firstContentLink) focusWithoutScroll(firstContentLink);
    }
  };

  const dismissMenu = (item, returnFocus = false) => {
    const state = states.get(item);
    const { toggle } = getParts(item);
    if (!state) return;

    // An explicit close invalidates every pending switch, including a
    // neighbour's aim timer, so no stale callback can reopen a menu.
    cancelAllPending();
    latchHover();
    state.explicit = false;
    state.dismissed = true;
    item.classList.add('mega-menu--dismissed');
    setExpanded(item, false);

    if (returnFocus) focusWithoutScroll(state.invoker || toggle);
  };

  const initializeSidebar = (menu) => {
    if (initializedSidebars.has(menu)) return;

    const categories = Array.from(menu.querySelectorAll('.mega-menu__sidebar-category'));
    const panels = Array.from(menu.querySelectorAll('.mega-menu__sidebar-panel'));
    if (categories.length === 0 || panels.length === 0) return;

    initializedSidebars.add(menu);

    const panelFor = (category) => {
      const panelId = category.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      return panel && menu.contains(panel) ? panel : null;
    };

    const activateCategory = (category, fromPointer = false) => {
      const targetPanel = panelFor(category);
      if (!targetPanel) return;

      const focusedPanel = panels.find((panel) => panel.contains(document.activeElement));
      if (fromPointer && focusedPanel && focusedPanel !== targetPanel) return;

      categories.forEach((candidate) => {
        const active = candidate === category;
        candidate.classList.toggle('mega-menu__sidebar-category--active', active);
        candidate.setAttribute('aria-expanded', String(active));
      });
      panels.forEach((panel) => {
        const active = panel === targetPanel;
        panel.classList.toggle('mega-menu__sidebar-panel--active', active);
        panel.hidden = !active;
      });
    };

    categories.forEach((category) => {
      category.addEventListener('focusin', () => activateCategory(category));

      if ('PointerEvent' in window) {
        category.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') activateCategory(category, true);
        });
      } else {
        category.addEventListener('mouseenter', () => activateCategory(category, true));
      }

      category.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight') return;
        const panel = panelFor(category);
        const firstLink = panel?.querySelector('a[href]');
        if (!firstLink) return;
        event.preventDefault();
        activateCategory(category);
        focusWithoutScroll(firstLink);
      });
    });
  };

  const initializeItem = (item) => {
    if (states.has(item)) return;

    const { link, toggle, menu } = getParts(item);
    if (!link || !toggle || !menu) return;

    const state = {
      hovered: item.matches(':hover'),
      explicit: false,
      dismissed: false,
      invoker: toggle,
      leaveTimer: null,
      aimTimer: null,
      shiftResetTimer: null,
    };
    states.set(item, state);
    trackedItems.add(item);

    const enterNow = () => {
      state.aimTimer = null;
      if (!item.isConnected) return;
      state.hovered = true;
      state.dismissed = false;
      item.classList.remove('mega-menu--dismissed');
      syncItem(item);
    };

    const leaveNow = () => {
      state.leaveTimer = null;
      if (!item.isConnected) return;
      state.hovered = false;
      if (!item.contains(document.activeElement)) {
        state.explicit = false;
        state.dismissed = false;
        item.classList.remove('mega-menu--dismissed');
      }
      syncItem(item);
    };

    const onPointerEnter = (event) => {
      if (blockedByLatch(event)) return;
      rememberPointer(event);
      warmUp();
      window.clearTimeout(state.leaveTimer);
      state.leaveTimer = null;
      window.clearTimeout(state.aimTimer);
      state.aimTimer = null;
      if (openItem && openItem !== item && movingIntoPanel()) {
        state.aimTimer = window.setTimeout(enterNow, MOTION.aimDelayMs);
        return;
      }
      enterNow();
    };

    const onPointerLeave = (event) => {
      rememberPointer(event);
      window.clearTimeout(state.aimTimer);
      state.aimTimer = null;
      if (item.classList.contains('mega-menu--open')) {
        window.clearTimeout(state.leaveTimer);
        state.leaveTimer = window.setTimeout(leaveNow, MOTION.closeGraceMs);
        return;
      }
      leaveNow();
    };

    if ('PointerEvent' in window) {
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') onPointerEnter(event);
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') onPointerLeave(event);
      });
    } else {
      item.addEventListener('mouseenter', onPointerEnter);
      item.addEventListener('mouseleave', onPointerLeave);
    }

    toggle.addEventListener('click', () => {
      if (state.explicit && !state.dismissed) {
        dismissMenu(item, false);
      } else {
        openMenu(item, toggle, false);
      }
    });

    [link, toggle].forEach((trigger) => {
      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        openMenu(item, trigger, true);
      });
    });

    menu.addEventListener('focusin', () => {
      if (state.dismissed) return;
      state.explicit = true;
      syncItem(item);
    });

    item.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!item.isConnected) {
          if (openItem === item) openItem = null;
          return;
        }
        if (item.contains(document.activeElement)) return;
        state.explicit = false;
        if (!state.hovered) {
          state.dismissed = false;
          item.classList.remove('mega-menu--dismissed');
        }
        syncItem(item);
      }, 0);
    });

    const closeButton = menu.querySelector('.mega-menu__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => dismissMenu(item, true));
    }

    if (menu.classList.contains('mega-menu--sidebar')) initializeSidebar(menu);
    syncItem(item);
  };

  const initializeMegaMenus = (root = document) => {
    if (openItem && !openItem.isConnected) openItem = null;

    if (root.matches?.('[data-mega-menu-item]')) initializeItem(root);
    root.querySelectorAll?.('[data-mega-menu-item]').forEach(initializeItem);
  };

  document.addEventListener('click', (event) => {
    if (!openItem || openItem.contains(event.target)) return;

    const item = openItem;
    const state = states.get(item);
    if (!state) return;
    cancelAllPending();
    latchHover(event);
    state.explicit = false;
    state.dismissed = state.hovered;
    item.classList.toggle('mega-menu--dismissed', state.dismissed);
    syncItem(item);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    cancelAllPending();
    if (!openItem) return;

    const item = openItem;
    const { toggle } = getParts(item);
    if (toggle?.getAttribute('aria-expanded') !== 'true') return;

    event.preventDefault();
    event.stopPropagation();
    dismissMenu(item, true);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeMegaMenus(event.target || document);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target;
    trackedItems.forEach((item) => {
      if (!section?.contains(item)) return;
      cancelPending(item);
      cancelShiftReset(item);
      const { menu } = getParts(item);
      if (menu && resizeObserver) resizeObserver.unobserve(menu);
      trackedItems.delete(item);
    });
    if (openItem && section?.contains(openItem)) {
      openItem = null;
      setTrailActive(false);
      if (stage) stage.classList.remove('mega-stage--open');
    }
    if (stage && section?.contains(stage)) {
      removeStage();
    }
  });

  window.addEventListener('resize', () => {
    const menu = openItem && getParts(openItem).menu;
    if (!menu) return;
    if (!menu.offsetParent) {
      // Desktop navigation is hidden (mobile/tablet drawer takes over).
      // Reset the whole disclosure state, not only classes and ARIA: WebKit
      // fires no focusout for the hidden toggle, so a stale `explicit` flag
      // would turn the next click after resizing back into a close.
      cancelAllPending();
      trackedItems.forEach((item) => {
        const state = states.get(item);
        if (!state) return;
        state.explicit = false;
        state.hovered = false;
        state.dismissed = false;
        item.classList.remove('mega-menu--dismissed');
      });
      setExpanded(openItem, false);
      return;
    }
    if (stage) placeStage(menu);
  }, { passive: true });

  window.__drhDesktopMegaMenuController = { init: initializeMegaMenus };
  rootElement.classList.add('mega-motion');
  initializeMegaMenus(document);

  // Menus without images can be laid out early so the first open animates;
  // menus with images wait for real intent (pointer or focus on the header).
  const header = document.querySelector('[data-mega-menu-item]')?.closest('header, .header');
  if (header) {
    header.addEventListener('pointerenter', warmUp, { once: true });
    header.addEventListener('focusin', warmUp, { once: true });
  }
  if (!document.querySelector('.mega-menu img, .mega-menu picture, .mega-menu video, .mega-menu iframe')) {
    (window.requestIdleCallback || ((callback) => window.setTimeout(callback, 200)))(warmUp);
  }
})();

// Mobile Submenu Toggle
document.querySelectorAll('.mobile-menu__toggle').forEach((toggle) => {
  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    const submenu = toggle.closest('.mobile-menu__item').querySelector('.mobile-menu__submenu');

    toggle.setAttribute('aria-expanded', !expanded);
    if (submenu) {
      submenu.hidden = expanded;
    }
  });
});

// Third menu level toggle (mega menu + mobile menu)
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-nav-l3-toggle], [data-mobile-group-toggle]');
  if (!toggle) return;
  const list = document.getElementById(toggle.getAttribute('aria-controls'));
  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
  if (list) list.hidden = expanded;
});
